/*
 * ॐ नमः शिवाय
 *
 * Copyright (c) 2025 Rudra Coresoft Technologies Private Limited
 *
 * This software is licensed under the VIJAYA 1.0 License.
 * See the LICENSE file for the full text.
 */

/**
 * Remembers that this visitor has already entered an event, so a reload does
 * not offer them a Register button they have already used.
 *
 * WHY THIS EXISTS AT ALL, and what it is not. The action-button path recorded
 * completion in the visitor's SERVER session, which survived a reload because
 * the cookie did. Registration now writes through the batch endpoint, which
 * records nothing of the sort — so without this, every refresh offers entry
 * again to somebody who already has their passes.
 *
 * THE RIGHT ANSWER IS TO READ IT BACK FROM THE DATA: ask which registrations
 * this person holds for this event. That answer is true on every device, after
 * any cache clear, and forever. It is not possible today: reading
 * event_registration needs either a public table (it is private) or an RBAC
 * read grant — and because the table carries `is_global` to permit the batch
 * write, such a grant would return EVERY visitor's registrations rather than
 * this one's. A flag that means only "may create through the batch endpoint"
 * would separate the two and unblock the read.
 *
 * IT RECORDS WHOSE ENTRY IT IS. The key is per application and event, but the
 * value carries the application user id, and the page drops a marker whose id is
 * not the person now signed in. Without that, a marker written by one person was
 * read by the next: after a logout — or a session that simply expired — the
 * browser still said "You are registered", and offered no way in. A marker with
 * no id is one written before this existed, and is treated the same way: not
 * ours, so it goes.
 *
 * So this is a per-browser convenience, and it is written to be obviously that:
 *
 *   • it survives a reload, which is what it is for;
 *   • it does NOT travel to another device or another browser;
 *   • clearing site data forgets it;
 *   • it is never consulted to decide whether a write is allowed — only whether
 *     a button is drawn. Somebody who clears it and registers again gets a
 *     second seat, which the database is perfectly happy to give them.
 */

const PREFIX = 'athlohive:entered';

/** Namespaced per application, so two apps on one origin cannot collide. */
function keyFor(organizationId, applicationId, eventId) {
  return `${PREFIX}:${organizationId}:${applicationId}:${eventId}`;
}

/**
 * Records that this visitor entered this event.
 *
 * Every access is wrapped: a private window, blocked site data or a full quota
 * all throw, and none of them is a reason to fail a registration that has
 * already been written. Losing the marker costs a redundant button, not a seat.
 */
export function rememberEntered(organizationId, applicationId, eventId, seats, userId) {
  try {
    window.localStorage.setItem(
      keyFor(organizationId, applicationId, eventId),
      JSON.stringify({
        at: new Date().toISOString(),
        seats: Number(seats) || 0,
        // Who this marker belongs to. Absent when the caller does not know, and
        // an absent id is never treated as a match — see belongsTo.
        userId: userId === undefined || userId === null ? null : Number(userId),
      }),
    );
  } catch (e) {
    // Deliberately silent — see above.
  }
}

/** What we remember about this event, or null. */
export function recallEntered(organizationId, applicationId, eventId) {
  try {
    const raw = window.localStorage.getItem(
      keyFor(organizationId, applicationId, eventId),
    );
    if (!raw) return null;

    const parsed = JSON.parse(raw);
    // Anything that is not the shape we wrote is treated as absent rather than
    // trusted: this value is editable by whoever owns the browser.
    if (!parsed || typeof parsed !== 'object') return null;
    return {
      at: parsed.at || null,
      seats: Number(parsed.seats) || 0,
      userId: parsed.userId === undefined || parsed.userId === null
        ? null : Number(parsed.userId),
    };
  } catch (e) {
    return null;
  }
}

/**
 * Whether a marker was written by the person now signed in.
 *
 * False for a marker with no id (written before markers carried one) and false
 * when nobody is signed in — in both cases it cannot be shown to belong to this
 * visitor, and showing somebody else's entry is the failure being avoided.
 */
export function belongsTo(marker, userId) {
  if (!marker || marker.userId === null || marker.userId === undefined) return false;
  if (userId === null || userId === undefined || userId === '') return false;
  return Number(marker.userId) === Number(userId);
}


/** Forgets one event — used when a registration is cancelled. */
export function forgetEntered(organizationId, applicationId, eventId) {
  try {
    window.localStorage.removeItem(keyFor(organizationId, applicationId, eventId));
  } catch (e) {
    // Silent, as above.
  }
}
